/**
 * Spot / wood-knot detection for kraft liner.
 *
 * Goal: only real visible defects (ตาไม้ / จุดดำชัด), not paper texture.
 * Soft multi-scale response + strong NMS + texture-relative threshold.
 */

function luminance(r, g, b) {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

function indexOf(x, y, width) {
  return (y * width + x) * 4;
}

function classifyDefectType(meanR, meanG, meanB, diameterMm, contrast, soft) {
  const brownness = meanR - meanB;
  const isBrownish = brownness > 3 && meanR >= meanG - 14;

  // LFB-QAD-SP-004 Table 3: จุดด่างดำ (เยื่อ) อนุญาตได้ถ้า ≤5 มม.
  // ตาไม้ = ห้าม — ติดเฉพาะเมื่อเด่นชัด ไม่ใช่จุดด่างดำนุ่มทั่วไป
  if (!soft && isBrownish && diameterMm >= 4.5 && contrast >= 9) {
    return 'wood_knot';
  }
  if (soft && isBrownish && diameterMm >= 6 && contrast >= 8) {
    return 'wood_knot';
  }
  return 'black_spot';
}

function isPenInk(meanR, meanG, meanB) {
  const blue =
    meanB > meanR + 12 &&
    meanB > meanG + 8 &&
    meanB > 65 &&
    meanB / Math.max(1, (meanR + meanG) / 2) > 1.1;
  const purple =
    meanB > meanG + 12 &&
    meanR > meanG + 5 &&
    meanB > 55 &&
    meanR > 45;
  return blue || purple;
}

/** เส้นขีด / ส่วนโค้งของวง — ไม่ใช่รอยด่างกลม */
function isStrokeLike(area, bw, bh, aspect, compactness) {
  if (aspect > 1.65) return true;
  if (aspect > 1.4 && compactness < 0.48) return true;
  if (compactness < 0.36) return true;
  const ellipseArea = Math.PI * (bw / 2) * (bh / 2);
  const fill = area / Math.max(1, ellipseArea);
  if (fill < 0.38 && aspect > 1.25) return true;
  return false;
}

function makeLocalMean(integ, width, height) {
  return function localMean(x, y, r) {
    const rr = Math.max(1, r | 0);
    const x1 = Math.max(0, x - rr);
    const y1 = Math.max(0, y - rr);
    const x2 = Math.min(width - 1, x + rr);
    const y2 = Math.min(height - 1, y + rr);
    const A = integ[y1 * (width + 1) + x1];
    const B = integ[y1 * (width + 1) + (x2 + 1)];
    const C = integ[(y2 + 1) * (width + 1) + x1];
    const D = integ[(y2 + 1) * (width + 1) + (x2 + 1)];
    const area = (x2 - x1 + 1) * (y2 - y1 + 1);
    return (D - B - C + A) / Math.max(1, area);
  };
}

/**
 * Soft stains / รอยด่าง (ตาไม้จาง รวม): กลางมืดกว่าพื้นแบบนุ่ม ไม่ต้องดำคม
 */
function findSoftBlobs(gray, data, width, height, integ, options) {
  const { mmPerPixel, maxSpotDiameterMm, paperLevel, edgePad } = options;
  const localMean = makeLocalMean(integ, width, height);

  // รอยด่างมัก ~2–8 มม. (+ สเกล 9 สำหรับรอยใหญ่ชัดเท่านั้น)
  const scalesMm = [2.2, 3.2, 4.5, 6.5, 9];
  const response = new Float32Array(width * height);
  const bestScale = new Float32Array(width * height);
  const step = Math.max(1, Math.round(0.45 / mmPerPixel));

  const noiseSamples = [];

  for (const scaleMm of scalesMm) {
    const rIn = Math.max(2, Math.round(scaleMm / (2 * mmPerPixel)));
    const rOut = Math.max(rIn + 3, Math.round(rIn * 2.8));

    for (let y = edgePad; y < height - edgePad; y += step) {
      for (let x = edgePad; x < width - edgePad; x += step) {
        const inner = localMean(x, y, rIn);
        const outer = localMean(x, y, rOut);
        const resp = outer - inner;
        if (noiseSamples.length < 2500 && (x + y) % (step * 4) === 0) {
          noiseSamples.push(resp);
        }
        // รอยด่างคอนทราสต์ต่ำกว่าจุดดำคม — สเกลใหญ่ต้องเด่นกว่า
        const minResp = scaleMm >= 9 ? 8.5 : 5.5;
        if (resp < minResp) continue;
        if (inner > paperLevel - 3) continue;
        const p = y * width + x;
        if (resp > response[p]) {
          response[p] = resp;
          bestScale[p] = scaleMm;
        }
      }
    }
  }

  noiseSamples.sort((a, b) => a - b);
  const p90 = noiseSamples[Math.floor(noiseSamples.length * 0.9)] || 4.5;
  const p95 = noiseSamples[Math.floor(noiseSamples.length * 0.95)] || 6;
  // สูงกว่า texture นิดหน่อย — พอรอยด่าง แต่ไม่มั่วทั้งหน้า
  const minPeak = Math.max(7.0, p95 + 1.6, p90 + 2.4);

  const nmsR = Math.max(4, Math.round(9 / mmPerPixel));
  const peaks = [];

  for (let y = edgePad; y < height - edgePad; y += step) {
    for (let x = edgePad; x < width - edgePad; x += step) {
      const p = y * width + x;
      const v = response[p];
      if (v < minPeak) continue;

      let isMax = true;
      for (let dy = -nmsR; dy <= nmsR && isMax; dy += step) {
        for (let dx = -nmsR; dx <= nmsR; dx += step) {
          if (dx === 0 && dy === 0) continue;
          const xx = x + dx;
          const yy = y + dy;
          if (xx < 0 || yy < 0 || xx >= width || yy >= height) continue;
          if (response[yy * width + xx] > v + 0.01) {
            isMax = false;
            break;
          }
        }
      }
      if (!isMax) continue;

      const scaleMm = bestScale[p] || 3.2;
      const growR = Math.max(3, Math.round((scaleMm * 0.75) / mmPerPixel));
      const aroundLarge = localMean(x, y, Math.round(15 / mmPerPixel));
      let minX = x;
      let maxX = x;
      let minY = y;
      let maxY = y;
      let sumR = 0;
      let sumG = 0;
      let sumB = 0;
      let lumSum = 0;
      let n = 0;

      for (let yy = y - growR; yy <= y + growR; yy += 1) {
        for (let xx = x - growR; xx <= x + growR; xx += 1) {
          if (xx < edgePad || yy < edgePad || xx >= width - edgePad || yy >= height - edgePad) {
            continue;
          }
          if (Math.hypot(xx - x, yy - y) > growR) continue;
          const g = gray[yy * width + xx];
          // รอยด่าง: มืดกว่าพื้นรอบๆ เล็กน้อยก็พอ
          if (g > aroundLarge - 3.5) continue;
          if (g > paperLevel - 2) continue;
          const i = indexOf(xx, yy, width);
          sumR += data[i];
          sumG += data[i + 1];
          sumB += data[i + 2];
          lumSum += g;
          n += 1;
          if (xx < minX) minX = xx;
          if (xx > maxX) maxX = xx;
          if (yy < minY) minY = yy;
          if (yy > maxY) maxY = yy;
        }
      }

      if (n < 10) continue;

      const meanR = sumR / n;
      const meanG = sumG / n;
      const meanB = sumB / n;
      if (isPenInk(meanR, meanG, meanB)) continue;

      const blobMean = lumSum / n;
      const contrast = aroundLarge - blobMean;
      if (contrast < 4.5) continue;
      if (blobMean > paperLevel - 4) continue;

      const bw = maxX - minX + 1;
      const bh = maxY - minY + 1;
      const aspect = Math.max(bw, bh) / Math.max(1, Math.min(bw, bh));
      const compactness = Math.min(1, n / Math.max(1, bw * bh));
      // ตัดเส้นวงปากกา/ดินสอ — เหลือแต่รอยด่างกลมๆ
      if (isStrokeLike(n, bw, bh, aspect, compactness)) continue;
      if (aspect > 1.55) continue;
      if (compactness < 0.42) continue;

      // รอยด่างจริงกลางมืด; เส้นวงมักมืดแค่ขอบ
      const centerLum = gray[y * width + x];
      let ringSum = 0;
      let ringN = 0;
      const ringR = Math.max(2, Math.round(growR * 0.85));
      for (let a = 0; a < 16; a += 1) {
        const ang = (a / 16) * Math.PI * 2;
        const xx = Math.round(x + Math.cos(ang) * ringR);
        const yy = Math.round(y + Math.sin(ang) * ringR);
        if (xx < 0 || yy < 0 || xx >= width || yy >= height) continue;
        ringSum += gray[yy * width + xx];
        ringN += 1;
      }
      const ringMean = ringN ? ringSum / ringN : aroundLarge;
      if (ringMean - centerLum < 2.5 && contrast < 7) continue;

      let diameterMm = Math.max(bw, bh) * mmPerPixel;
      const measured = diameterMm;
      diameterMm = Math.min(
        Math.max(diameterMm, scaleMm * 0.65),
        scaleMm >= 9 ? scaleMm * 1.35 : scaleMm * 1.15
      );
      // รอยใหญ่จริง (bbox ใหญ่ + contrast สูง) ใช้ขนาดวัดได้
      if (scaleMm >= 9 && measured >= scaleMm * 0.9 && contrast >= 8) {
        diameterMm = Math.min(measured, maxSpotDiameterMm * 2.2);
      }
      diameterMm = Math.max(2.0, Math.min(diameterMm, maxSpotDiameterMm * 1.8));
      if (diameterMm < 2.0) continue;
      if (diameterMm > maxSpotDiameterMm * 1.6 && contrast < 9) continue;

      peaks.push({
        soft: true,
        stain: true,
        score: v * (1 + contrast * 0.2) * Math.sqrt(diameterMm) * compactness,
        diameterMm: Number(diameterMm.toFixed(2)),
        xMm: Number((x * mmPerPixel).toFixed(2)),
        yMm: Number((y * mmPerPixel).toFixed(2)),
        areaPx: n,
        contrast: Number(contrast.toFixed(1)),
        bbox: { minX, minY, maxX, maxY },
        meanR,
        meanG,
        meanB,
        aspect,
        compactness,
      });
    }
  }

  return peaks;
}

/**
 * จุดดำคมจริงๆ เท่านั้น — ไม่ใช้จับรอยด่าง (กันมั่วฝุ่น)
 */
function findCompactDarkSpots(gray, data, width, height, integ, options) {
  const { mmPerPixel, maxSpotDiameterMm, paperLevel, edgePad } = options;
  const localMean = makeLocalMean(integ, width, height);

  const localDrop = 22;
  const maxAreaPx = Math.max(
    4500,
    Math.round(Math.PI * Math.pow(maxSpotDiameterMm * 1.8 / mmPerPixel / 2, 2))
  );
  const mask = new Uint8Array(width * height);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const v = gray[y * width + x];
      const m = localMean(x, y, 10);
      mask[y * width + x] =
        v < m - localDrop && v < paperLevel - 20 ? 1 : 0;
    }
  }

  const visited = new Uint8Array(width * height);
  const stack = new Int32Array(width * height);
  const out = [];

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const start = y * width + x;
      if (!mask[start] || visited[start]) continue;

      let top = 0;
      stack[top++] = start;
      visited[start] = 1;

      let minX = x;
      let maxX = x;
      let minY = y;
      let maxY = y;
      let area = 0;
      let sumX = 0;
      let sumY = 0;
      let lumSum = 0;
      let sumR = 0;
      let sumG = 0;
      let sumB = 0;

      while (top > 0) {
        const p = stack[--top];
        const px = p % width;
        const py = (p / width) | 0;
        area += 1;
        sumX += px;
        sumY += py;
        lumSum += gray[p];
        const i = indexOf(px, py, width);
        sumR += data[i];
        sumG += data[i + 1];
        sumB += data[i + 2];
        if (px < minX) minX = px;
        if (px > maxX) maxX = px;
        if (py < minY) minY = py;
        if (py > maxY) maxY = py;

        const neighbors = [p - 1, p + 1, p - width, p + width];
        for (let n = 0; n < 4; n += 1) {
          const np = neighbors[n];
          if (np < 0 || np >= mask.length) continue;
          const nx = np % width;
          const ny = (np / width) | 0;
          if (Math.abs(nx - px) + Math.abs(ny - py) !== 1) continue;
          if (!mask[np] || visited[np]) continue;
          visited[np] = 1;
          stack[top++] = np;
        }
      }

      if (area < 18 || area > maxAreaPx) continue;
      if (
        minX <= edgePad ||
        minY <= edgePad ||
        maxX >= width - 1 - edgePad ||
        maxY >= height - 1 - edgePad
      ) {
        continue;
      }

      const bw = maxX - minX + 1;
      const bh = maxY - minY + 1;
      const aspect = Math.max(bw, bh) / Math.max(1, Math.min(bw, bh));
      if (aspect > 2.0) continue;
      const compactness = area / (bw * bh);
      if (compactness < 0.4) continue;
      if (isStrokeLike(area, bw, bh, aspect, compactness)) continue;

      const diameterMm = Math.max(bw, bh) * mmPerPixel;
      if (diameterMm < 2.5 || diameterMm > maxSpotDiameterMm * 3) continue;

      const meanR = sumR / area;
      const meanG = sumG / area;
      const meanB = sumB / area;
      if (isPenInk(meanR, meanG, meanB)) continue;

      const cx = Math.round(sumX / area);
      const cy = Math.round(sumY / area);
      const blobMean = lumSum / area;
      const around = localMean(cx, cy, Math.max(14, Math.round(Math.max(bw, bh) * 2.5)));
      const contrast = around - blobMean;
      if (contrast < 20) continue;
      if (blobMean > paperLevel - 22) continue;

      const pad = Math.max(1, Math.round(0.35 / mmPerPixel));
      out.push({
        soft: false,
        stain: false,
        score: contrast * diameterMm * 0.9,
        diameterMm: Number(diameterMm.toFixed(2)),
        xMm: Number((cx * mmPerPixel).toFixed(2)),
        yMm: Number((cy * mmPerPixel).toFixed(2)),
        areaPx: area,
        contrast: Number(contrast.toFixed(1)),
        bbox: {
          minX: Math.max(0, minX - pad),
          minY: Math.max(0, minY - pad),
          maxX: Math.min(width - 1, maxX + pad),
          maxY: Math.min(height - 1, maxY + pad),
        },
        meanR,
        meanG,
        meanB,
        aspect,
        compactness,
      });
    }
  }

  return out;
}

/**
 * รอยด่างใหญ่ชัด (กลางแผ่น) — flood-fill จาก pixel มืดจริง ไม่พึ่ง local peak
 */
function findLargeObviousStains(gray, data, width, height, integ, options) {
  const { mmPerPixel, maxSpotDiameterMm, paperLevel, edgePad } = options;
  const localMean = makeLocalMean(integ, width, height);
  const innerPad = Math.max(edgePad, Math.floor(Math.min(width, height) * 0.1));
  const darkTh = paperLevel - 22;
  const mask = new Uint8Array(width * height);

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (x < innerPad || y < innerPad || x >= width - innerPad || y >= height - innerPad) {
        continue;
      }
      const v = gray[y * width + x];
      if (v >= darkTh) continue;
      const i = indexOf(x, y, width);
      if (isPenInk(data[i], data[i + 1], data[i + 2])) continue;
      mask[y * width + x] = 1;
    }
  }

  const visited = new Uint8Array(width * height);
  const stack = new Int32Array(width * height);
  const out = [];

  for (let y = innerPad; y < height - innerPad; y += 1) {
    for (let x = innerPad; x < width - innerPad; x += 1) {
      const start = y * width + x;
      if (!mask[start] || visited[start]) continue;

      let top = 0;
      stack[top++] = start;
      visited[start] = 1;

      let minX = x;
      let maxX = x;
      let minY = y;
      let maxY = y;
      let area = 0;
      let sumX = 0;
      let sumY = 0;
      let lumSum = 0;
      let sumR = 0;
      let sumG = 0;
      let sumB = 0;

      while (top > 0) {
        const p = stack[--top];
        const px = p % width;
        const py = (p / width) | 0;
        area += 1;
        sumX += px;
        sumY += py;
        lumSum += gray[p];
        const i = indexOf(px, py, width);
        sumR += data[i];
        sumG += data[i + 1];
        sumB += data[i + 2];
        if (px < minX) minX = px;
        if (px > maxX) maxX = px;
        if (py < minY) minY = py;
        if (py > maxY) maxY = py;

        const neighbors = [p - 1, p + 1, p - width, p + width];
        for (let n = 0; n < 4; n += 1) {
          const np = neighbors[n];
          if (np < 0 || np >= mask.length) continue;
          if (!mask[np] || visited[np]) continue;
          visited[np] = 1;
          stack[top++] = np;
        }
      }

      if (area < 80 || area > 6500) continue;

      const bw = maxX - minX + 1;
      const bh = maxY - minY + 1;
      const aspect = Math.max(bw, bh) / Math.max(1, Math.min(bw, bh));
      const compactness = area / (bw * bh);
      if (aspect > 1.55 || compactness < 0.32) continue;

      const diameterMm = Math.max(bw, bh) * mmPerPixel;
      if (diameterMm < 5 || diameterMm > maxSpotDiameterMm * 3) continue;

      const meanR = sumR / area;
      const meanG = sumG / area;
      const meanB = sumB / area;
      if (isPenInk(meanR, meanG, meanB)) continue;

      const cx = Math.round(sumX / area);
      const cy = Math.round(sumY / area);
      const blobMean = lumSum / area;
      const outerR = Math.max(12, Math.round(Math.max(bw, bh) * 2.2));
      const around = localMean(cx, cy, outerR);
      const contrast = around - blobMean;
      if (contrast < 5.5) continue;
      if (blobMean > paperLevel - 18) continue;

      out.push({
        soft: true,
        stain: true,
        score: contrast * diameterMm * 2.5,
        diameterMm: Number(diameterMm.toFixed(2)),
        xMm: Number((cx * mmPerPixel).toFixed(2)),
        yMm: Number((cy * mmPerPixel).toFixed(2)),
        areaPx: area,
        contrast: Number(contrast.toFixed(1)),
        bbox: { minX, minY, maxX, maxY },
        meanR,
        meanG,
        meanB,
        aspect,
        compactness,
      });
    }
  }

  out.sort((a, b) => b.score - a.score);
  return out.slice(0, 4);
}

export function detectBlackSpots(imageData, options = {}) {
  const {
    mmPerPixel = 0.2,
    maxSpotDiameterMm = 5,
    largeSpotThresholdMm = 5,
    minDistanceBetweenLargeSpotsCm = 8,
    maxBlackSpotsPerSqFt = 5,
    sampleAreaSqFt = 1,
    /** One spot every ~10 mm */
    nmsDistMm = 10,
    maxSpots = 60,
  } = options;

  const { data, width, height } = imageData;
  const gray = new Float32Array(width * height);
  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    gray[p] = luminance(data[i], data[i + 1], data[i + 2]);
  }

  const integ = new Float64Array((width + 1) * (height + 1));
  for (let y = 1; y <= height; y += 1) {
    let row = 0;
    for (let x = 1; x <= width; x += 1) {
      row += gray[(y - 1) * width + (x - 1)];
      integ[y * (width + 1) + x] = integ[(y - 1) * (width + 1) + x] + row;
    }
  }

  const probe = [];
  for (let y = 0; y < height; y += 4) {
    for (let x = 0; x < width; x += 4) {
      probe.push(gray[y * width + x]);
    }
  }
  probe.sort((a, b) => a - b);
  const paperLevel = probe[Math.floor(probe.length * 0.6)] || 140;
  const edgePad = Math.max(6, Math.floor(Math.min(width, height) * 0.08));

  const shared = {
    mmPerPixel,
    maxSpotDiameterMm,
    paperLevel,
    edgePad,
  };

  const candidates = [
    ...findSoftBlobs(gray, data, width, height, integ, shared),
    ...findCompactDarkSpots(gray, data, width, height, integ, shared),
    ...findLargeObviousStains(gray, data, width, height, integ, shared),
  ];

  candidates.sort((a, b) => b.score - a.score);

  const topScore = candidates[0]?.score || 0;
  const borderPx = Math.floor(Math.min(width, height) * 0.11);
  const strong = candidates.filter((c) => {
    const largeCenter =
      c.stain && c.diameterMm >= 5 && c.contrast >= 5.5 && c.areaPx >= 80;
    if (largeCenter) return true;

    if (c.bbox) {
      const cxPx = (c.bbox.minX + c.bbox.maxX) / 2;
      const cyPx = (c.bbox.minY + c.bbox.maxY) / 2;
      const nearEdge =
        cxPx < borderPx ||
        cxPx > width - borderPx ||
        cyPx < borderPx ||
        cyPx > height - borderPx;
      // ขอบลอนคลื่นมักมั่ว — ยกเว้นเฉพาะรอยใหญ่ชัด
      if (nearEdge && c.contrast < 12 && c.diameterMm < 11) return false;
    }

    if (c.score < topScore * 0.4) return false;
    if (c.stain || c.soft) return c.contrast >= 4.5 && c.diameterMm >= 2.0;
    return c.contrast >= 18 && c.diameterMm >= 2.5;
  });

  const merged = [];
  for (const c of strong) {
    const near = merged.some(
      (k) => Math.hypot(k.xMm - c.xMm, k.yMm - c.yMm) < nmsDistMm
    );
    if (near) continue;

    const type = classifyDefectType(
      c.meanR,
      c.meanG,
      c.meanB,
      c.diameterMm,
      c.contrast,
      Boolean(c.soft)
    );

    merged.push({
      id: `spot-${merged.length + 1}`,
      type,
      diameterMm: c.diameterMm,
      xMm: c.xMm,
      yMm: c.yMm,
      areaPx: c.areaPx,
      contrast: c.contrast,
      bbox: c.bbox,
      soft: Boolean(c.soft),
    });
  }

  const limited = merged.slice(0, maxSpots);

  const defects = limited
    .filter((s) => s.type === 'wood_knot')
    .map((s) => ({
      type: 'wood_knot',
      relatedSpotId: s.id,
      diameterMm: s.diameterMm,
    }));

  const oversized = limited.filter((s) => s.diameterMm > maxSpotDiameterMm);
  const large = limited.filter((s) => s.diameterMm > largeSpotThresholdMm);
  const distanceViolations = [];
  for (let i = 0; i < large.length; i += 1) {
    for (let j = i + 1; j < large.length; j += 1) {
      const a = large[i];
      const b = large[j];
      const distCm = Math.hypot(a.xMm - b.xMm, a.yMm - b.yMm) / 10;
      if (distCm < minDistanceBetweenLargeSpotsCm) {
        distanceViolations.push({
          a: a.id,
          b: b.id,
          distanceCm: Number(distCm.toFixed(2)),
        });
      }
    }
  }

  const spotCount = limited.length;
  const areaLabel = Number(sampleAreaSqFt.toFixed(2));
  const spotsPerSqFt =
    sampleAreaSqFt > 0 ? spotCount / sampleAreaSqFt : spotCount;
  const countExceeded = spotCount > maxBlackSpotsPerSqFt;

  const failures = [];
  if (defects.length) {
    failures.push({
      code: 'ZERO_TOLERANCE_DEFECT',
      message: 'พบตาไม้ / ตำหนิที่ไม่อนุญาต',
      items: defects,
    });
  }
  if (oversized.length) {
    failures.push({
      code: 'SPOT_TOO_LARGE',
      message: `จุดด่างดำเส้นผ่านศูนย์กลางเกิน ${maxSpotDiameterMm} มม.`,
      items: oversized,
    });
  }
  if (countExceeded) {
    failures.push({
      code: 'SPOT_COUNT_EXCEEDED',
      message: `จำนวนจุดเกินเกณฑ์ (พบ ${spotCount} จุด จากที่อนุญาต ${maxBlackSpotsPerSqFt} จุด)`,
      count: spotCount,
    });
  }
  if (distanceViolations.length) {
    failures.push({
      code: 'SPOT_DISTANCE_TOO_CLOSE',
      message: `จุดขนาดใหญ่กว่า ${largeSpotThresholdMm} มม. ระยะห่างน้อยกว่า ${minDistanceBetweenLargeSpotsCm} ซม.`,
      items: distanceViolations,
    });
  }

  return {
    verdict: failures.length ? 'NG' : 'OK',
    spots: limited,
    defects,
    failures,
    metrics: {
      width,
      height,
      bgMean: Number(paperLevel.toFixed(1)),
      threshold: 'knot-strict',
      mmPerPixel: Number(mmPerPixel.toFixed(4)),
      spotCount,
      sampleAreaSqFt: areaLabel,
      spotsPerSqFt: Number(spotsPerSqFt.toFixed(2)),
      softCount: limited.filter((s) => s.soft).length,
      processingMs: 0,
    },
  };
}
